import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'wouter';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import Header from '@/components/layout/header';
import { Plus, ChartLine, Loader2, FileImage, ChevronRight } from 'lucide-react';
import PlotCard from '@/components/gallery/plot-card';
import { useQuery } from '@tanstack/react-query';
import type { FileObject } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';
import { authorizedFetch, scopedKey } from '@/lib/queryClient';
import { useProjectGallery } from '@/hooks/use-project-gallery';

export default function ProjectGallery() {
  const renderId = useRef(Math.random().toString(36).slice(2, 8));
  const renderStart = useRef(performance.now());
  
  const { projectId } = useParams();
  const { go } = useNav();
  const { user } = useAuth();
  const { currentProject, setCurrentProject, projects, loadProjects } = useProjectsStore() as any;
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'title'>('recent');
  const [filterDataset, setFilterDataset] = useState<string>('all');
  const { toast } = useToast();
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Use the new gallery hook with cursor-based pagination
  const gallery = useProjectGallery({
    projectId,
    search,
    sort: sortBy,
    datasetId: filterDataset !== 'all' ? filterDataset : null,
    limit: 20,
    enabled: !!projectId && !!user,
  });

  // Track render performance
  useEffect(() => {
    const renderMs = performance.now() - renderStart.current;
    if (renderMs > 100) {
      console.log(`[gallery:frontend:render:${renderId.current}] slow render`, {
        renderMs: Math.round(renderMs),
        itemsCount: gallery.items.length,
        isLoading: gallery.isLoading,
      });
    }
    renderStart.current = performance.now();
  });

  // Align plot count with Dashboard by using the same summary endpoint
  useQuery({
    queryKey: scopedKey([user?.id, '/api/projects', projectId, 'summary']),
    enabled: !!projectId,
  });

  // Load datasets for filtering and labeling
  const { data: files = [] } = useQuery<FileObject[]>({
    queryKey: scopedKey([user?.id, '/api/projects', projectId, 'files']),
    enabled: !!projectId,
  });

  // Initial project load and projects list (for Move menu)
  useEffect(() => {
    if (!user) {
      go(routes.home());
      return;
    }
    if (!projectId) return;
    
    // Run all initial loads in parallel for faster page load
    (async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          authorizedFetch(`/api/projects/${projectId}`)
            .then(res => res.json())
            .then(project => setCurrentProject(project))
            .catch(() => go(routes.dashboard())),
          loadProjects().catch(() => {}),
        ]);
      } catch {}
      setIsInitialLoading(false);
    })();
  }, [projectId, user, setCurrentProject, loadProjects, go]);

  // Listen for saves/duplicates to invalidate React Query cache
  useEffect(() => {
    const onSaved = (e: any) => {
      try {
        const pid = e?.detail?.projectId;
        if (!projectId || (pid && pid !== projectId)) return;
        gallery.invalidate();
      } catch {}
    };
    const onDuplicated = (e: any) => {
      try {
        const pid = e?.detail?.projectId;
        if (!projectId || (pid && pid !== projectId)) return;
        gallery.invalidate();
      } catch {}
    };
    window.addEventListener('lumiplot-save-success' as any, onSaved);
    window.addEventListener('lumiplot-duplicated' as any, onDuplicated);
    return () => {
      window.removeEventListener('lumiplot-save-success' as any, onSaved);
      window.removeEventListener('lumiplot-duplicated' as any, onDuplicated);
    };
  }, [projectId, gallery]);

  // Map gallery items to PlotCard format
  const datasetCacheRef = useRef<Map<string, { id: string | null; name?: string }>>(new Map());
  const getFileName = (file?: FileObject) => {
    if (!file) return undefined;
    const meta = (file.metadata as Record<string, any> | null | undefined) ?? {};
    return meta.originalName || meta.filename || file.objectPath;
  };

  const mapStart = performance.now();
  const mappedItems = gallery.items.map((item) => {
    // Cache dataset info for navigation
    if (item.datasetFileId || item.datasetName) {
      datasetCacheRef.current.set(item.id, {
        id: item.datasetFileId,
        name: item.datasetName || undefined,
      });
    }

    return {
      id: item.id,
      title: item.title || 'Untitled Plot',
      createdAt: new Date(item.createdAt),
      thumb_url: item.thumbnailUrl || item.preview || undefined,
      datasetName: item.datasetName || undefined,
      datasetFileId: item.datasetFileId,
      plotSpec: item.chartType ? { plotType: item.chartType } : undefined,
    };
  });
  const mapMs = performance.now() - mapStart;
  
  if (mapMs > 50 && mappedItems.length > 0) {
    console.log(`[gallery:frontend] map items slow`, {
      itemsCount: mappedItems.length,
      mapMs: Math.round(mapMs),
    });
  }

  const handleCreateNew = () => {
    go(routes.plotEditor.new());
  };

  const handleViewPlot = (plotId: string) => {
    go(routes.plotEditor.open(projectId as string, plotId) + '?tab=plot');
  };

  const handleEditPlot = (plotId: string) => {
    go(routes.plotEditor.open(projectId as string, plotId));
  };

  const handleDuplicatePlot = async (plot: any) => {
    setDuplicatingId(String(plot?.id || ''));
    const progressToast = toast({
      title: 'Copying plot…',
      description: 'This may take a little while. A new canvas will open as soon as it\'s ready.'
    });
    try {
      if (!projectId) return;
      // Stash seed figure from latest snapshot for hydration in editor
      try {
        const figRes = await authorizedFetch(`/api/ai/v2/plots/${plot.id}/versions/latest/plotly_figure.json`);
        if (figRes.ok) {
          const fig = await figRes.json();
          try { localStorage.setItem(`lumiplot:dupseed:${plot.id}`, JSON.stringify(fig)); } catch {}
        }
      } catch {}
      const res = await authorizedFetch(`/api/ai/v2/plots/${plot.id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId })
      });
      const j = await res.json();
      if ((j as any)?.success && (j as any)?.newPlotId) {
        try { window.dispatchEvent(new CustomEvent('lumiplot-duplicated', { detail: { projectId } })); } catch {}
        // Hydrate immediately via seedFrom; editor also falls back to latest snapshot
        try { progressToast.dismiss(); } catch {}
        go(routes.plotEditor.open(projectId as string, (j as any).newPlotId) + `?seedFrom=plot:${plot.id}`);
      } else {
        throw new Error((j as any)?.error || 'Duplicate failed');
      }
    } catch (err) {
      try { progressToast.dismiss(); } catch {}
      toast({ title: 'Duplicate failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setDuplicatingId(null);
    }
  };

  // Actions
  const handleRenamePlot = async (plotId: string, newTitle: string) => {
    try {
      const res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle })
      });
      const j = await res.json();
      if (!res.ok || !(j?.success)) throw new Error(j?.error || 'Rename failed');
      // Invalidate gallery to refresh with new title
      gallery.invalidate();
    } catch (err) {
      toast({ title: 'Rename failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const handleDeletePlot = async (plotId: string) => {
    try {
      const res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Delete failed');
      gallery.invalidate();
    } catch (err) {
      toast({ title: 'Delete failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const handleMovePlot = async (plotId: string, targetProjectId: string) => {
    try {
      const res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetProjectId })
      });
      const j = await res.json();
      if (!res.ok || !j?.success) throw new Error('Move failed');
      // Navigate to editor in target project with the moved plot
      const targetPid = j?.projectId || targetProjectId;
      const movedPlotId = j?.plotId || plotId;
      go(routes.plotEditor.open(String(targetPid), String(movedPlotId)));
      // Invalidate gallery to refresh counts/UI
      gallery.invalidate();
    } catch (err) {
      toast({ title: 'Move failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const handleAddToFigure = (plotId: string) => {
    if (!projectId) return;
    // Navigate to new figure page with plotId query param
    go(routes.project.figures.new(projectId) + `?plotId=${plotId}`);
  };

  const isLoading = isInitialLoading || gallery.isLoading;

  if (isLoading && mappedItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center" role="status" aria-busy="true" aria-live="polite">
            <div className="inline-flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm font-medium">Loading</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (gallery.isError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <h3 className="text-xl font-medium text-destructive mb-2">Failed to load gallery</h3>
            <p className="text-muted-foreground mb-4">
              {gallery.error instanceof Error ? gallery.error.message : 'Unknown error'}
            </p>
            <Button onClick={() => gallery.refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Button variant="ghost" size="sm" onClick={() => go(routes.dashboard())}>
            Projects
          </Button>
          <ChevronRight className="h-4 w-4" />
          <Button variant="ghost" size="sm" onClick={() => go(routes.project.root(projectId!))}>
            {currentProject?.name || 'Project'}
          </Button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">Plot Gallery</span>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Plot Gallery</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage your plots
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => go(routes.project.figures.list(projectId!))}>
              <FileImage className="mr-2 h-4 w-4" />
              Multi-panel
            </Button>
            <Button onClick={handleCreateNew}>
              <Plus className="mr-2 h-4 w-4" />
              Create New Plot
            </Button>
          </div>
        </div>

        {/* Controls */}
        <Card className="mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plots..."
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          >
            <option value="recent">Sort: Recent</option>
            <option value="title">Sort: Title</option>
          </select>
          <select
            value={filterDataset}
            onChange={(e) => setFilterDataset(e.target.value)}
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          >
            <option value="all">Filter: All datasets</option>
            {files.map((f) => (
              <option key={f.id} value={String(f.id)}>{getFileName(f) || f.id}</option>
            ))}
          </select>
          </div>
        </Card>

        {/* Plots Grid (Published snapshots only) */}
        {mappedItems.length === 0 ? (
          <div className="text-center py-16">
            <ChartLine className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-medium text-muted-foreground mb-2">
              No plots yet
            </h3>
            <p className="text-muted-foreground mb-6">
              Create your first plot to see it here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mappedItems.map((plot) => (
              <PlotCard
                key={plot.id}
                plot={plot as any}
                isDuplicating={duplicatingId === String(plot.id)}
                datasetName={(plot as any).datasetName}
                onDatasetClick={async () => {
                  try {
                    if (!projectId) return;
                    const cached = datasetCacheRef.current.get(plot.id);
                    let datasetId = cached?.id ?? (plot as any).datasetFileId ?? null;
                    let datasetName = cached?.name ?? (plot as any).datasetName;

                    if (!datasetId) {
                      try {
                        const res = await authorizedFetch(`/api/ai/v2/plots/${plot.id}/versions/latest/meta.json`);
                        if (res.ok) {
                          const meta = await res.json();
                          datasetId = meta?.datasetFileId || meta?.dataset_file_id || datasetId;
                          datasetName = datasetName ?? meta?.datasetName ?? meta?.dataset_name;
                        }
                      } catch (err) {
                        console.warn('Failed to load meta for dataset navigation', err);
                      }
                    }

                    if (!datasetId && datasetName) {
                      const match = files.find((f) => String(getFileName(f)).toLowerCase() === String(datasetName).toLowerCase());
                      if (match) datasetId = String(match.id);
                    }

                    if (datasetId) {
                      datasetCacheRef.current.set(plot.id, { id: datasetId, name: datasetName });
                      go(routes.project.file(projectId as string, datasetId));
                    } else {
                      toast({ title: 'Dataset unavailable', description: 'Could not locate the dataset for this plot.', variant: 'destructive' });
                    }
                  } catch (err) {
                    console.error('Dataset navigation failed', err);
                    toast({ title: 'Dataset unavailable', description: 'Could not locate the dataset for this plot.', variant: 'destructive' });
                  }
                }}
                onView={() => handleViewPlot(plot.id)}
                onEdit={() => handleEditPlot(plot.id)}
                onDuplicate={() => handleDuplicatePlot(plot)}
                onRename={(t) => handleRenamePlot(plot.id, t)}
                onDelete={() => handleDeletePlot(plot.id)}
                onMove={(targetId) => handleMovePlot(plot.id, targetId)}
                onAddToFigure={() => handleAddToFigure(plot.id)}
                projectId={projectId}
                projectOptions={(projects || []).map((p: any) => ({ id: p.id, name: p.name }))}
              />
            ))}
          </div>
        )}

        {/* Load More / Pagination */}
        {gallery.hasNextPage && (
          <div className="flex items-center justify-center mt-6">
            <Button
              variant="outline"
              onClick={() => gallery.fetchNextPage()}
              disabled={gallery.isLoading}
            >
              {gallery.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
